import { extender } from "@shared/backend/class-extender";
import HyperEntity from "@shared/backend/hyper-entity.decorator";
import { JsonField } from "@shared/helpers/custom-fields";
import { Fields, Relations } from "remult";
import { CommunicationRecord } from "../communication-record";
import { OrganizationManager } from "../organization-manager";
import { VolunteerOrganizationContact } from "../volunteer-organization-contact";
import { VolunteerOrgActivity } from "../volunteer-org-activity";
import { VolunteerOrgProjectAssignment } from "../volunteer-org-project-assignment";
import { VolunteerOrganizationLead } from "../volunteer-organization-lead";
import { VolunteerOrganizationEntityConfig } from "./config";
import { VolunteerOrganizationFilters } from "./filters";
import { VolunteerOrganizationMethods } from "./methods";

@HyperEntity("volunteer-organization", VolunteerOrganizationEntityConfig)
export class VolunteerOrganization extends extender(
  VolunteerOrganizationFilters,
  VolunteerOrganizationMethods,
) {
  @Fields.id()
  id = "";

  @Fields.string({ caption: "שם" })
  name = "";

  @Fields.string({ allowNull: true, caption: "תיאור" })
  description?: string | null;

  @Fields.string({
    dbName: "relationship_type",
    allowNull: true,
    caption: "סוג הקשר",
  })
  relationshipType?: string | null;

  @Fields.string({
    dbName: "parent_id",
    allowNull: true,
    caption: "ארגון אב",
  })
  parentId?: string | null;

  @Relations.toOne<VolunteerOrganization, VolunteerOrganization>(
    () => VolunteerOrganization,
    "parentId",
  )
  parent?: VolunteerOrganization;

  @Relations.toMany(() => VolunteerOrganization, {
    field: "parentId",
    caption: "שלוחות",
  })
  branches?: VolunteerOrganization[];

  @Fields.string({ dbName: "head_name", allowNull: true, caption: "ראש" })
  headName?: string | null;

  @Fields.string({ dbName: "head_phone", allowNull: true, caption: "טלפון ראש" })
  headPhone?: string | null;

  @Fields.string({
    dbName: "volunteering_coordinator_name",
    allowNull: true,
    caption: "אחראי התנדבות",
  })
  volunteeringCoordinatorName?: string | null;

  @Fields.string({
    dbName: "volunteering_coordinator_phone",
    allowNull: true,
    caption: "טלפון אחראי התנדבות",
  })
  volunteeringCoordinatorPhone?: string | null;

  @Fields.string({
    dbName: "emergency_contact_name",
    allowNull: true,
    caption: "איש קשר חירום",
  })
  emergencyContactName?: string | null;

  @Fields.string({
    dbName: "emergency_contact_phone",
    allowNull: true,
    caption: "טלפון חירום",
  })
  emergencyContactPhone?: string | null;

  @Fields.string({
    dbName: "extra_contact_name",
    allowNull: true,
    caption: "איש קשר נוסף",
  })
  extraContactName?: string | null;

  @Fields.string({
    dbName: "extra_contact_phone",
    allowNull: true,
    caption: "טלפון נוסף",
  })
  extraContactPhone?: string | null;

  @Fields.string({ caption: "הערות פנימיות" })
  comments = "";

  @Fields.string({
    dbName: "handling_manager_id",
    allowNull: true,
    caption: "מנהל מלווה",
  })
  handlingManagerId?: string | null;

  @Relations.toOne<VolunteerOrganization, OrganizationManager>(
    () => OrganizationManager,
    "handlingManagerId",
  )
  handlingManager?: OrganizationManager;

  @Fields.string({ allowNull: true, caption: "כתובת" })
  address?: string | null;

  @JsonField<VolunteerOrganization, { lat: number; lng: number }>({
    allowedKeys: ["lat", "lng"],
    allowNull: true,
  })
  coordinates?: { lat: number; lng: number } | null;

  @Fields.integer({
    dbName: "volunteer_capacity",
    allowNull: true,
    caption: "קיבולת מתנדבים",
  })
  volunteerCapacity?: number | null;

  @Fields.boolean({
    dbName: "interested_in_routine",
    caption: "אירוע שגרתי",
  })
  interestedInRoutine = false;

  @Fields.boolean({
    dbName: "interested_in_outbreak",
    caption: "אירוע מתפרץ",
  })
  interestedInOutbreak = false;

  @Fields.boolean({
    dbName: "interested_in_emergency",
    caption: "חירום",
  })
  interestedInEmergency = false;

  @Relations.toMany(() => VolunteerOrganizationContact, {
    field: "volunteerOrganizationId",
    caption: "אנשי קשר",
  })
  contacts?: VolunteerOrganizationContact[];

  @Relations.toMany(() => VolunteerOrgActivity, {
    field: "volunteerOrganizationId",
    caption: "תחומים",
  })
  activities?: VolunteerOrgActivity[];

  @Relations.toMany(() => VolunteerOrgProjectAssignment, {
    field: "volunteerOrganizationId",
    caption: "שיבוצים",
  })
  assignments?: VolunteerOrgProjectAssignment[];

  @Relations.toMany(() => CommunicationRecord, "volunteerOrganizationId")
  communicationRecords?: CommunicationRecord[];

  @Relations.toMany(() => VolunteerOrganizationLead, {
    field: "volunteerOrganizationId",
  })
  convertedFromLeads?: VolunteerOrganizationLead[];

  @Fields.createdAt({ dbName: "created_at", caption: "תאריך יצירה" })
  createdAt = new Date();

  @Fields.updatedAt({ dbName: "updated_at", caption: "תאריך עדכון" })
  updatedAt = new Date();
}
